import React, { useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import configData from "../config.json";
import { GetSession, GetToken } from '../components/Session';
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { FormDateTimePicker, FormDropDownList, FormSwitch, FormNumericTextBox } from "../components/formComponents";
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { GetPermissions } from '../components/Options';
import { Button, ButtonGroup } from '@progress/kendo-react-buttons';
import { TextBox } from '@progress/kendo-react-inputs';
import { Upload } from '@progress/kendo-react-upload';
import ExpansionPanel from "../components/ExpansionPanel";

const CommandCell = (props) => {
    const { dataItem } = props;
    return (
        <td>
            <Button icon="arrow-up" onClick={() => props.moveUp(dataItem)} title="Save" />
            <Button icon="arrow-down" onClick={() => props.moveDown(dataItem)} title="Move Down" />
            <Button icon="delete" onClick={() => props.remove(dataItem)} title="Delete Message" />
        </td>
    )
}

const EditKioskPlaylists = () => {
    const { id } = useParams(); // editroles/id url is used to edit existing role, otherwise id is undefined
    const saveName = id === undefined ? "Create" : "Update";
    const [formkey, setFormkey] = React.useState(0); // seems to be needed to push values to form after loading
    const [message, setMessage] = React.useState('');
    const [data, setData] = React.useState([]);
    const [permissions, setPermissions] = React.useState(null);
    const [textMode, setTextMode] = React.useState(true);
    const periods = ["None", "Daily", "Weekly", "Monthly", "Yearly"];
    const requestIntervalRef = React.useRef({});
    const [files, setFiles] = React.useState([]);
    const progressRef = React.useRef({});
    const [uploadMessage, setUploadMessage] = React.useState("");
    const navigate = useNavigate();
    const [submitDisabled, setSubmitDisabled] = React.useState(false);
    const [expandedPanels, setExpandedPanels] = useState({
        playlistDetails: true,
        playlistMessages: false
    });

    const togglePanel = (panel) => {
        setExpandedPanels(prev => ({ ...prev, [panel]: !prev[panel] }));
    };

    // default layout data for page
    const [layout, setLayout] = React.useState([
        { "field": "startDate", "label": "Date", "defaultValue": new Date(), "isVisible": true, "type": "text" },
        { "field": "repetitionPeriod", "label": "Repetition Period", "defaultValue": "None", "isVisible": true, "type": "text" },
        { "field": "repetitionValue", "label": "Repetition Value", "defaultValue": 0, "isVisible": true, "type": "numeric" },
        { "field": "isActive", "label": "Active", "defaultValue": true, "isVisible": true, "type": "boolean" },
        { "field": "displayOrder", "label": "DisplayOrder", "defaultValue": 1, "isVisible": true, "type": "numeric" },
        { "field": "duration", "label": "Play sequence for (days)", "defaultValue": 5, "isVisible": true, "type": "numeric" },
        { "field": "playlistDetails", "label": "Details", "defaultValue": "", "isVisible": true, "type": "heading" },
        { "field": "playlistMessages", "label": "Messages", "defaultValue": "", "isVisible": true, "type": "heading" },
        { "field": "newMessage", "label": "Add New Message", "defaultValue": "", "isVisible": true, "type": "heading" },
        { "field": "messageDuration", "label": "Message Duration", "defaultValue": 5, "isVisible": true, "type": "numeric" }
    ]);

    // Gets field from layout based on field name
    const GetField = (f) => {
        return layout.find(obj => { return obj.field === f });
    }

    // State data for form. Values match form value on submit
    const [values, setValues] = React.useState({
        startDate: GetField("startDate")["defaultValue"],
        displayOrder: GetField("displayOrder")["defaultValue"],
        repetitionPeriod: GetField("repetitionPeriod")["defaultValue"],
        repetitionValue: GetField("repetitionValue")["defaultValue"],
        duration: GetField("duration")["defaultValue"],
        messageDuration: GetField("messageDuration")["defaultValue"],
        isActive: String(GetField("isActive")["defaultValue"]).toLowerCase() === "true"
    });

    // When page first loads:
    // - populate drop downs from api/options
    // - if in edit mode, set initial values for all fields for api/drivers
    React.useEffect(() => {
        GetPermissions(["ViewKioskPlaylists", "NewKioskPlaylists", "EditKioskPlaylists"], setPermissions);
        var url = configData.SERVER_URL + "apiv7/userinterfaces?$filter=viewName eq 'editKioskPlaylists'";
        const init = {
            method: 'GET',
            accept: 'application/json',
            headers: { "Authorization": "Bearer " + GetToken() }
        }
        fetch(url, init).then(response => response.json()).then(d => {
            if (GetSession().IsMobile) {
                let data = [];
                for (let i = 0; i < d.length; i++) {
                    data.push({ field: d[i].field, label: d[i].label, type: d[i].type, format: d[i].format, isVisible: d[i].isVisible && d[i].isVisibleOnMobile });
                }
                setLayout(data);
            } else {
                setLayout(d);
            }
        });
        if (id !== undefined) {
            url = `${configData.SERVER_URL}apiv7/kioskplaylists/${id}`;
            fetch(url, init).then(response => response.json()).then(d => {
                let dt = d.startDate === null || d.startDate == undefined ? new Date() : new Date(d.startDate + "Z");
                setValues({
                    startDate: dt,
                    duration: d.duration,
                    repetitionPeriod: d.repetitionPeriod,
                    repetitionValue: d.repetitionValue,
                    messageDuration: d.messageDuration,
                    displayOrder: d.displayOrder === null ? 1 : d.displayOrder,
                    isActive: d.isActive
                });
                setFormkey(formkey + 1);
                UpdateTable();
            })
        }
    }, []);

    // Returns value to be sent to web api:
    // If field is visible just return f
    // If field is hidden, return defaultValue from layout data
    const GetDefaultData = (f, name) => {
        let fld = GetField(name);
        if (fld === undefined || fld === null) return f;
        if (fld["isVisible"]) return f;
        let res = "";
        switch (fld["type"]) {
            case "text": res = fld["defaultValue"]; break;
            case "numeric": res = parseFloat(fld["defaultValue"]); break;
            case "boolean": res = fld["defaultValue"] === "true"; break;
            default: res = fld["defaultValue"]; break;
        }
        return res;
    }

    // Handle submit button. For create POST the data, for edit PUT the data, then redirect to offenses index page.
    const handleSubmit = () => {
        let dataItem = { ...values };
        var pth = id === undefined ? "" : "/" + id;
        setSubmitDisabled(true);
        var url = `${configData.SERVER_URL}apiv7/kioskplaylists${pth}`;
        const init = {
            method: id === undefined ? 'POST' : 'PUT',
            accept: "application/json",
            headers: {
                "Content-type": "application/json",
                "Authorization": "Bearer " + GetToken()
            },
            mode: "cors",
            cache: "no-cache",
            body: JSON.stringify({
                "kioskPlaylistId": id === undefined ? 0 : id,
                "displayOrder": GetDefaultData(dataItem.displayOrder, "displayOrder"),
                "startDate": GetDefaultData(dataItem.startDate, "startDate"),
                "repetitionPeriod": GetDefaultData(dataItem.repetitionPeriod, "repetitionPeriod"),
                "repetitionValue": GetDefaultData(dataItem.repetitionValue, "repetitionValue"),
                "messageDuration": GetDefaultData(dataItem.messageDuration, "messageDuration"),
                "duration": GetDefaultData(dataItem.duration, "duration"),
                "isActive": GetDefaultData(dataItem.isActive, "isActive")
            })
        }
        fetch(url, init).then(response => response.json()).then(d => {
            if (id === undefined && d.kioskPlaylistId !== undefined) {
                navigate(`/editkioskplaylists/${d.kioskPlaylistId}`, { state: { msg: msg } });
            } else {
                var msg = id === undefined ? "New playlist created" : "Playlist updated";
                Redirect(msg);
            }
        });
    }

    // navigation
    const GoBack = () => Redirect("");
    const Redirect = (msg = "") => {
        navigate("/kioskplaylists", { state: { msg: msg } });
    }
    if (permissions && !permissions.CanView) navigate("/error401");;
    if (permissions && !permissions.CanEdit && id !== undefined) navigate("/error401");;
    if (permissions && !permissions.CanCreate && id === undefined) navigate("/error401");;

    const UpdateTable = () => {
        const init = {
            method: 'GET',
            accept: 'application/json',
            headers: { "Authorization": "Bearer " + GetToken() }
        }
        let url = `${configData.SERVER_URL}apiv7/kioskmessages?$filter=kioskPlaylistId eq ${id}`;
        fetch(url, init).then(response => response.json()).then(d => {
            setData(d);
        })
    }

    // form validators
    const textValidator = (value) => !value ? "Please enter a value" : "";

    const ActionCell = (props) => {
        return (<CommandCell {...props} moveUp={MoveUp} moveDown={MoveDown} remove={Remove} />)
    }

    const ContentCell = (props) => {
        let d = props.dataItem;
        if (d.content.indexOf("data:image") == 0)
            return (<td><img src={d.content} style={{ maxWidth: "500px", maxHeight: "300px" }} /></td>);
        else
            return (<td>{d.content}</td>);
    }

    const MoveUp = (itm) => {
        MessageCommand(itm, "MoveUp");
    }

    const MoveDown = (itm) => {
        MessageCommand(itm, "MoveDown");
    }

    const Remove = (itm) => {
        MessageCommand(itm, "Delete");
    }

    const MessageCommand = (itm, cmd) => {
        const init = {
            method: 'PATCH',
            accept: 'application/json',
            headers: { "Authorization": "Bearer " + GetToken() }
        }
        let url = `${configData.SERVER_URL}apiv7/kioskmessages?cmd=${cmd}&id=${itm.kioskMessageId}`;
        fetch(url, init).then(response => response.json()).then(d => {
            setData(d);
        })
    }

    const textChanged = (e) => { setMessage(e.value) }

    const AddMessage = () => {
        UploadMessage(message);
    }

    const UploadMessage = (m) => {
        const init = {
            method: 'POST',
            accept: 'application/json',
            headers: {
                "Content-type": "application/json",
                "Authorization": "Bearer " + GetToken()
            },
            body: JSON.stringify({
                kioskPlaylistId: id,
                content: m,
                displayOrder: 0
            })
        }
        let url = `${configData.SERVER_URL}apiv7/kioskmessages`;
        fetch(url, init).then(response => {
            UpdateTable();
        })
    }

    const onSave = (files, o, onProgress) => {
        const currentFile = files[0];
        const uid = currentFile.uid;
        progressRef.current[uid] = 0;
        files.push(currentFile);

        // Simulate save request
        return new Promise((resolve, reject) => {
            if (currentFile.validationErrors && currentFile.validationErrors.length > 0) {
                reject({ uid: uid });
            } else {
                const reader = new FileReader();
                reader.onload = () => {
                    if (reader.result && typeof reader.result === 'string') {
                        CreateImageMessage(reader.result);
                        resolve({ uid: uid });
                    } else {
                        reject({ uid: uid });
                    }
                };
                reader.onprogress = (data) => {
                    onProgress(uid, data);
                };
                reader.onabort = () => {
                    reject({ uid: uid });
                };
                reader.onerror = () => {
                    reject({ uid: uid });
                };

                reader.readAsDataURL(currentFile.getRawFile());
            }
        });
    }

    const CreateImageMessage = (im) => {
        UploadMessage(im);
        setUploadMessage("Image uploaded");
        setTimeout(() => { setUploadMessage("") }, 3000);
    }

    const MessageDurationChanged = (e) => { let v = { ...values }; v.messageDuration = e.value; setValues(v) }
    const OrderChanged = (e) => { let v = { ...values }; v.displayOrder = e.value; setValues(v) }
    const ActiveChanged = (e) => { let v = { ...values }; v.isActive = e.value; setValues(v) }
    const StartDateChanged = (e) => { let v = { ...values }; v.startDate = e.value; setValues(v) }
    const RepPeriodChanged = (e) => { let v = { ...values }; v.repetitionPeriod = e.value; setValues(v) }
    const RepValueChanged = (e) => { let v = { ...values }; v.repetitionValue = e.value; setValues(v) }
    const DurationChanged = (e) => { let v = { ...values }; v.duration = e.value; setValues(v) }


    return (
        <div style={{ padding: 20, maxWidth: 1200, margin: "auto" }}>
            {GetField("playlistDetails")["isVisible"] && <ExpansionPanel title={GetField("playlistDetails")["label"]} expanded={expandedPanels.playlistDetails} onToggle={() => togglePanel("playlistDetails")}>
                    <Form initialValues={values} key={formkey} render={(formRenderProps) => (
                        <FormElement style={{ width: "98%" }}>
                            <fieldset className={"k-form-fieldset"}>
                                <div className="row">
                                    {GetField("messageDuration")["isVisible"] && <div className="col-md-2">
                                        <Field id={"messageDuration"} name={"messageDuration"} label={GetField("messageDuration")["label"]} component={FormNumericTextBox} validator={textValidator} min={1} onChange={MessageDurationChanged} />
                                    </div>}
                                    {GetField("displayOrder")["isVisible"] && <div className="col-md-2">
                                        <Field id={"displayOrder"} name={"displayOrder"} label={GetField("displayOrder")["label"]} component={FormNumericTextBox} validator={textValidator} min={0} onChange={OrderChanged} />
                                    </div>}
                                    {GetField("isActive")["isVisible"] && <div className='col-md-3'>
                                        <Field id={"isActive"} name={"isActive"} label={GetField("isActive")["label"]} component={FormSwitch} onChange={ActiveChanged} />
                                    </div>}
                                </div>
                                <div className="row">
                                    {GetField("startDate")["isVisible"] && <div className='col-md-2'>
                                        <Field id={"startDate"} name={"startDate"} label={GetField("startDate")["label"]} component={FormDateTimePicker}
                                            wrapperStyle={{ width: "90%", marginRight: "18px", }} onChange={StartDateChanged} />
                                    </div>}
                                    {GetField("repetitionValue")["isVisible"] && <div className='col-md-2'>
                                        <Field id={"repetitionValue"} name={"repetitionValue"} label={GetField("repetitionPeriod")["label"]} component={FormNumericTextBox} min={0} onChange={RepValueChanged} />
                                    </div>}
                                    {GetField("repetitionPeriod")["isVisible"] && <div className='col-md-2'>
                                        <Field id={"repetitionPeriod"} name={"repetitionPeriod"} label="&nbsp;" data={periods} component={FormDropDownList} onChange={RepPeriodChanged} />
                                    </div>}
                                    {GetField("duration")["isVisible"] && <div className='col-md-2'>
                                        <Field id={"duration"} name={"duration"} label={GetField("duration")["label"]} component={FormNumericTextBox} min={1} onChange={DurationChanged} />
                                    </div>}
                                </div>
                            </fieldset>
                            <div className="k-form-buttons" style={{ paddingTop: 10 }}>
                                <div className='col-md-1'>
                                <Button themeColor={"primary"} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={handleSubmit} disabled={submitDisabled}>{saveName}</Button>
                                </div>
                                <div className='col-md-1'>
                                    <Button className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={GoBack}>Back to List</Button>
                                </div>
                            </div>
                        </FormElement>
                    )} />
            </ExpansionPanel>}
            {GetField("playlistMessages")["isVisible"] && id !== undefined && <ExpansionPanel title={GetField("playlistMessages")["label"]} expanded={expandedPanels.playlistMessages} onToggle={() => togglePanel("playlistMessages")}>
                    <div className='row'>
                        <h4>{GetField("newMessage")["label"]}</h4>
                    </div>
                    <div className='row'>
                        <div className='col-md-3'>
                            <ButtonGroup>
                                <Button selected={textMode} togglable={true} onClick={() => setTextMode(true)}>Text</Button>
                                <Button selected={!textMode} togglable={true} onClick={() => setTextMode(false)}>Image</Button>
                            </ButtonGroup>
                        </div>
                    </div>
                    {textMode && <div>
                        <div className='row' style={{ padding: "10px 0px" }}>
                            <div className='col-md-12'>
                                <TextBox placeholder="Enter message text" onChange={textChanged}></TextBox>
                            </div>
                        </div>
                        <div className="k-form-buttons" style={{ padding: "10px 0px" }}>
                            <div className='col-md-1'>
                                <Button themeColor={"primary"} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={AddMessage}>Add Message</Button>
                            </div>
                        </div>
                    </div>}
                    {!textMode && <div>
                        <div className='row' style={{ padding: "10px 0px" }}>
                            <div className='col-md-6'>
                                <Upload multiple={false} saveUrl={onSave} />
                            </div>
                        </div>
                        <div className='row' style={{ padding: "10px 0px", color: "red" }}>
                            <div>{uploadMessage}</div>
                        </div>
                    </div>}
                    <Grid data={data}>
                        <GridColumn field="content" title="Content" cells={{ data: ContentCell }} />
                        <GridColumn field="displayOrder" title="Display Order" width="100px" />
                        <GridColumn field="action" title="Action" cells={{ data: ActionCell }} width="150px" />
                    </Grid>
            </ExpansionPanel>}
        </div>
    );
}

export default EditKioskPlaylists